import { useMemo, useState } from "react";
import { motion, MotionConfig, useReducedMotion } from "framer-motion";
import {
  MessageSquare,
  Bot,
  Zap,
  Star,
  BarChart3,
  TrendingUp,
  Activity,
  Filter,
  Layers,
  HelpCircle,
  PieChart as PieIcon,
  Smile,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import {
  containerVariants,
  itemVariants,
} from "@/features/dashboard/components/motion";
import { KpiCard } from "@/features/dashboard/components/KpiCard";
import { TimeRangeSelector } from "./components/TimeRangeSelector";
import { MessageVolumeChart } from "./components/MessageVolumeChart";
import { AutomationRateCard } from "./components/AutomationRateCard";
import { ResponseTimeChart } from "./components/ResponseTimeChart";
import { ResponseSpeedCard } from "./components/ResponseSpeedCard";
import { ConversionFunnel } from "./components/ConversionFunnel";
import { PlatformBreakdownTable } from "./components/PlatformBreakdownTable";
import { TopIntentsList } from "./components/TopIntentsList";
import { SentimentSplitCard } from "./components/SentimentSplitCard";
import { getEmptyAnalyticsData, type TimeRange } from "./mockAnalytics";
import { useAnalytics } from "./hooks/useAnalytics";

/**
 * Deep-dive Analytics page — metrics-heavy control surface styled with
 * ServerlessLens Pod cards, unified metric headers, and compact layout.
 */
export function AnalyticsPage() {
  const [range, setRange] = useState<TimeRange>("30d");
  const prefersReducedMotion = useReducedMotion();
  const fallback = useMemo(() => getEmptyAnalyticsData(range), [range]);
  const { data: analytics } = useAnalytics(range);
  const data = analytics ?? fallback;

  return (
    <MotionConfig reducedMotion="user">
      <div className="flex-1 bg-transparent p-4 md:p-6">
        <motion.div
          className="mx-auto max-w-7xl space-y-4 md:space-y-5"
          variants={containerVariants}
          initial={prefersReducedMotion ? false : "hidden"}
          animate="visible"
        >
          {/* Top Bar with TimeRangeSelector */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <BarChart3 className="h-3 w-3" />
              </span>
              <span className="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
                Performance &amp; Conversion Analytics
              </span>
            </div>
            <TimeRangeSelector value={range} onChange={setRange} />
          </div>

          {/* KPI Row: ServerlessLens Grouped Telemetry Pods */}
          <motion.div
            className="grid grid-cols-1 gap-3.5 lg:grid-cols-2"
            variants={containerVariants}
          >
            {/* Pod 1: Pipeline & Automation Telemetry */}
            <motion.div variants={itemVariants}>
              <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs">
                <div className="flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800 bg-slate-100/50 dark:bg-slate-850/60 px-3.5 py-2">
                  <div className="flex items-center gap-2">
                    <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <MessageSquare className="h-3 w-3" />
                    </span>
                    <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
                      Conversations &amp; Automation
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200/80 dark:border-emerald-800/60 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.2 text-[9px] font-bold text-emerald-700 dark:text-emerald-300">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                      Live Feed
                    </span>
                  </div>
                  <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">Omnichannel throughput</span>
                </div>

                <div className="grid grid-cols-2 gap-2.5 p-2.5 sm:p-3 bg-white/70 dark:bg-slate-900/70">
                  <KpiCard
                    title="Total Conversations"
                    value={data.summary.totalConversations}
                    trend={data.summary.totalConversationsTrendPct}
                    icon={<MessageSquare className="h-4 w-4" />}
                    tone="primary"
                    to="/conversations"
                    linkTitle="View all conversations"
                    bars={[40, 65, 85, 100, 75]}
                    barTone="primary"
                    description="Total prospective customer inquiries and ongoing conversations processed across all channels."
                  />
                  <KpiCard
                    title="AI Automation Rate"
                    value={data.summary.automation.automatedPct}
                    suffix="%"
                    trend={data.summary.automation.trendPct}
                    icon={<Bot className="h-4 w-4" />}
                    tone="success"
                    to="/automations"
                    linkTitle="View automations"
                    bars={[30, 50, 70, 95, 60]}
                    barTone="emerald"
                    description="Percentage of customer queries resolved autonomously by AI without requiring human agent escalation."
                  />
                </div>
              </Card>
            </motion.div>

            {/* Pod 2: Speed & Satisfaction Telemetry */}
            <motion.div variants={itemVariants}>
              <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs">
                <div className="flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800 bg-slate-100/50 dark:bg-slate-850/60 px-3.5 py-2">
                  <div className="flex items-center gap-2">
                    <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-indigo-500/10 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400">
                      <Zap className="h-3 w-3" />
                    </span>
                    <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
                      Speed &amp; Satisfaction
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full border border-indigo-200/80 dark:border-indigo-800/60 bg-indigo-50 dark:bg-indigo-950/40 px-2 py-0.2 text-[9px] font-bold text-indigo-700 dark:text-indigo-300">
                      Measured
                    </span>
                  </div>
                  <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">Real-time sync</span>
                </div>

                <div className="grid grid-cols-2 gap-2.5 p-2.5 sm:p-3 bg-white/70 dark:bg-slate-900/70">
                  <KpiCard
                    title="Avg First Response"
                    value={data.summary.firstResponseSeconds}
                    suffix="s"
                    decimals={1}
                    trend={data.summary.firstResponseTrendPct}
                    icon={<Zap className="h-4 w-4" />}
                    tone="accent"
                    bars={[90, 70, 50, 30, 20]}
                    barTone="emerald"
                    description="Average turnaround duration from customer message arrival to the initial automated AI reply."
                  />
                  <KpiCard
                    title="CSAT Score"
                    value={data.summary.csatScore}
                    suffix="/5"
                    decimals={1}
                    trend={data.summary.csatTrendPct}
                    icon={<Star className="h-4 w-4" />}
                    tone="warning"
                    bars={[45, 60, 80, 100, 85]}
                    barTone="amber"
                    description="Aggregated customer satisfaction rating scored across post-resolution feedback."
                  />
                </div>
              </Card>
            </motion.div>
          </motion.div>

          {/* Message Volume + Automation Rate */}
          <motion.div className="grid grid-cols-1 gap-4 lg:grid-cols-3" variants={containerVariants}>
            <motion.div className="lg:col-span-2" variants={itemVariants}>
              <Card className="max-w-none h-full overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs flex flex-col justify-between">
                <div className="flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800 bg-slate-100/50 dark:bg-slate-850/60 px-3.5 py-2">
                  <div className="flex items-center gap-2">
                    <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-blue-500/10 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400">
                      <TrendingUp className="h-3 w-3" />
                    </span>
                    <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
                      Message Volume by Platform
                    </span>
                  </div>
                  <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">Omnichannel throughput</span>
                </div>
                <div className="p-3 bg-white/70 dark:bg-slate-900/70 flex-1">
                  <MessageVolumeChart data={data.messageVolume} />
                </div>
              </Card>
            </motion.div>

            <motion.div variants={itemVariants}>
              <Card className="max-w-none h-full overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs flex flex-col justify-between">
                <div className="flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800 bg-slate-100/50 dark:bg-slate-850/60 px-3.5 py-2">
                  <div className="flex items-center gap-2">
                    <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-emerald-500/10 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400">
                      <PieIcon className="h-3 w-3" />
                    </span>
                    <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
                      AI Automation Share
                    </span>
                  </div>
                  <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">Bot vs. Human</span>
                </div>
                <div className="p-3 bg-white/70 dark:bg-slate-900/70 flex-1 flex flex-col justify-center">
                  <AutomationRateCard data={data.summary.automation} />
                </div>
              </Card>
            </motion.div>
          </motion.div>

          {/* Response Time + First Response vs Resolution */}
          <motion.div className="grid grid-cols-1 gap-4 lg:grid-cols-3" variants={containerVariants}>
            <motion.div className="lg:col-span-2" variants={itemVariants}>
              <Card className="max-w-none h-full overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs flex flex-col justify-between">
                <div className="flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800 bg-slate-100/50 dark:bg-slate-850/60 px-3.5 py-2">
                  <div className="flex items-center gap-2">
                    <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-sky-500/10 dark:bg-sky-500/20 text-sky-600 dark:text-sky-400">
                      <Activity className="h-3 w-3" />
                    </span>
                    <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
                      Response Time Trend — AI vs Human
                    </span>
                  </div>
                  <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">Speed comparison</span>
                </div>
                <div className="p-3 bg-white/70 dark:bg-slate-900/70 flex-1">
                  <ResponseTimeChart data={data.responseTime} />
                </div>
              </Card>
            </motion.div>

            <motion.div variants={itemVariants}>
              <Card className="max-w-none h-full overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs flex flex-col justify-between">
                <div className="flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800 bg-slate-100/50 dark:bg-slate-850/60 px-3.5 py-2">
                  <div className="flex items-center gap-2">
                    <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-amber-500/10 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400">
                      <Zap className="h-3 w-3" />
                    </span>
                    <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
                      First Response vs Resolution
                    </span>
                  </div>
                  <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">Resolution time</span>
                </div>
                <div className="p-3 bg-white/70 dark:bg-slate-900/70 flex-1 flex flex-col justify-center">
                  <ResponseSpeedCard
                    firstResponseSeconds={data.summary.firstResponseSeconds}
                    firstResponseTrendPct={data.summary.firstResponseTrendPct}
                    resolutionMinutes={data.summary.resolutionMinutes}
                    resolutionTrendPct={data.summary.resolutionTrendPct}
                  />
                </div>
              </Card>
            </motion.div>
          </motion.div>

          {/* Conversion Funnel */}
          <motion.div variants={itemVariants}>
            <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs">
              <div className="flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800 bg-slate-100/50 dark:bg-slate-850/60 px-3.5 py-2">
                <div className="flex items-center gap-2">
                  <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-purple-500/10 dark:bg-purple-500/20 text-purple-600 dark:text-purple-400">
                    <Filter className="h-3 w-3" />
                  </span>
                  <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
                    Conversion Funnel — Messages to Sale
                  </span>
                </div>
                <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">Stage drop-off tracking</span>
              </div>
              <div className="p-3.5 bg-white/70 dark:bg-slate-900/70">
                <ConversionFunnel data={data.funnel} />
              </div>
            </Card>
          </motion.div>

          {/* Platform Breakdown Table */}
          <motion.div variants={itemVariants}>
            <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs">
              <div className="flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800 bg-slate-100/50 dark:bg-slate-850/60 px-3.5 py-2">
                <div className="flex items-center gap-2">
                  <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Layers className="h-3 w-3" />
                  </span>
                  <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
                    Per-Platform Performance Breakdown
                  </span>
                </div>
                <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">Channel comparative metrics</span>
              </div>
              <div className="p-3 bg-white/70 dark:bg-slate-900/70">
                <PlatformBreakdownTable data={data.platforms} />
              </div>
            </Card>
          </motion.div>

          {/* Top Intents + Sentiment */}
          <motion.div className="grid grid-cols-1 gap-4 lg:grid-cols-2" variants={containerVariants}>
            <motion.div variants={itemVariants}>
              <Card className="max-w-none h-full overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs">
                <div className="flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800 bg-slate-100/50 dark:bg-slate-850/60 px-3.5 py-2">
                  <div className="flex items-center gap-2">
                    <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-indigo-500/10 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400">
                      <HelpCircle className="h-3 w-3" />
                    </span>
                    <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
                      Top Customer Intents
                    </span>
                  </div>
                  <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">Most frequent inquiries</span>
                </div>
                <div className="p-3 bg-white/70 dark:bg-slate-900/70">
                  <TopIntentsList data={data.topIntents} />
                </div>
              </Card>
            </motion.div>

            <motion.div variants={itemVariants}>
              <Card className="max-w-none h-full overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs">
                <div className="flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800 bg-slate-100/50 dark:bg-slate-850/60 px-3.5 py-2">
                  <div className="flex items-center gap-2">
                    <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-amber-500/10 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400">
                      <Smile className="h-3 w-3" />
                    </span>
                    <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
                      Customer Sentiment &amp; CSAT
                    </span>
                  </div>
                  <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">Satisfaction score</span>
                </div>
                <div className="p-3 bg-white/70 dark:bg-slate-900/70">
                  <SentimentSplitCard data={data.sentiment} />
                </div>
              </Card>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </MotionConfig>
  );
}
